import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { color, font } from 'src/shared/styles/variables';
import { ProviderType } from 'src/modules/user/models/user';
import { Button, Form, FormInputTextarea, pxToRem, useForm } from '@modernhealth/vital-components';

const Wrapper = styled.div({
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: 1000,
    maxWidth: 492,
    alignSelf: 'center',
});

const Title = styled.h3({
    marginBottom: 16,
    color: color.pickledBluewood,
    ...font.black(font.size.headerExtended),
});

const Description = styled.h5({
    textAlign: 'center',
    color: color.steelGray,
    ...font.book(font.size.large),
});

const StyledFormInputTextArea = styled(FormInputTextarea)({
    marginTop: pxToRem(16),
});

interface Props {
    anythingElse: string;
    onSubmit: (data: PreferencesFormData) => void;
    providerType?: ProviderType;
}

export type PreferencesFormData = { preferences: string };

export const RequestProvider: React.FC<Props> = ({
    onSubmit,
    anythingElse,
    providerType = ProviderType.COACH,
}) => {
    const { t } = useTranslation();

    const preferencesFormDefinition = useForm<PreferencesFormData>({
        defaultValues: { preferences: anythingElse },
    });

    return (
        <Wrapper>
            <Title id="PreferencesFormTitle">{t('providers:request_provider.title')}</Title>
            <Description>
                {t(
                    `providers:request_provider.description.${
                        providerType === ProviderType.THERAPIST ? 'therapist' : 'coach'
                    }`,
                )}
            </Description>

            <Form onSubmit={onSubmit} definition={preferencesFormDefinition}>
                <StyledFormInputTextArea
                    labelledBy="PreferencesFormTitle"
                    name="preferences"
                    placeholder={t('providers:request_provider.placeholder')}
                    rows={5}
                />
                <Button
                    busy={preferencesFormDefinition.formState.isSubmitting}
                    fluid
                    size="large"
                    type="submit"
                >
                    {t('providers:anything_else.submit')}
                </Button>
            </Form>
        </Wrapper>
    );
};
